import React, { useState } from 'react';
import { Chip, Menu, MenuItem, Tooltip } from '@mui/material';
import { COMMON_FRAME_RATES, formatFrameRate } from '../../utils/timecode';

interface FrameRateChipProps {
  fps: number;
  /**
   * True when the rate was read from the video file at upload. It is then a
   * fact about the footage and is shown without a picker.
   */
  known: boolean;
  /** True when the user picked `fps` themselves (only possible while unknown). */
  chosen?: boolean;
  onChange: (fps: number) => void;
}

const chipSx = { fontVariantNumeric: 'tabular-nums', fontWeight: 600, height: 24 } as const;

/**
 * Shows the frame rate timecodes are counted in. A rate read from the file is
 * shown as is; an unknown one is labelled "(estimated)" and can be set, so
 * frame numbers and exported markers match the footage.
 */
const FrameRateChip: React.FC<FrameRateChipProps> = ({ fps, known, chosen = false, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = `${formatFrameRate(fps)} fps`;

  if (known) {
    return (
      <Tooltip title="Frame rate read from the video file when it was uploaded">
        <Chip
          size="small"
          variant="outlined"
          label={label}
          aria-label={`Frame rate ${label}`}
          data-testid="frame-rate-chip"
          sx={chipSx}
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip
        title={chosen
          ? 'The frame rate you chose for this video. Click to change it.'
          : 'The frame rate wasn’t recorded for this video. Choose the right one for exact frame numbers.'}
      >
        <Chip
          size="small"
          variant="outlined"
          label={chosen ? label : `${label} (estimated)`}
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={`Frame rate ${label}${chosen ? '' : ' (estimated)'}. Change frame rate`}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          data-testid="frame-rate-chip"
          sx={chipSx}
        />
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} MenuListProps={{ 'aria-label': 'Frame rate' }}>
        {COMMON_FRAME_RATES.map((rate) => (
          <MenuItem
            key={rate}
            selected={Math.abs(rate - fps) < 0.001}
            onClick={() => {
              onChange(rate);
              setAnchor(null);
            }}
            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 120 }}
          >
            {formatFrameRate(rate)} fps
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FrameRateChip;
